import * as c from "@yahshi/contracts";
import { z } from "zod";
import { createAdminRequest, type AdminHttpOptions } from "../../../lib/adminHttp";
import { M1ApiError } from "../../m1-app/data/M1AppApi";

export interface SmsConfigurationApi {
  list(query?: Partial<c.SmsConfigurationListQuery>): Promise<c.SmsConfigurationPage>;
  get(id: string): Promise<c.SmsConfiguration>;
  create(input: c.SmsConfigurationCreateInput): Promise<c.SmsConfiguration>;
  update(id: string, input: c.SmsConfigurationUpdateInput): Promise<c.SmsConfiguration>;
  publish(id: string, input: c.SmsConfigurationPublishInput): Promise<c.SmsConfiguration>;
  rollback(id: string, input: c.SmsConfigurationRollbackInput): Promise<c.SmsConfiguration>;
  setStatus(id: string, input: c.SmsConfigurationStatusInput): Promise<c.SmsConfiguration>;
  revisions(id: string, query?: Partial<c.SmsPagination>): Promise<z.infer<typeof c.smsConfigurationRevisionPageSchema>>;
  getBinding(appId: string): Promise<c.SmsBinding>;
  bind(appId: string, input: c.SmsBindingUpdateInput): Promise<c.SmsBinding>;
  deliveries(query?: Partial<c.SmsDeliveryQuery>): Promise<z.infer<typeof c.smsDeliveryPageSchema>>;
  runtime(): Promise<c.SmsRuntimeStatus>;
  runtimeSummary(): Promise<c.SmsRuntimeSummary>;
  readiness(): Promise<c.SmsReadiness>;
  getRuntimeControl(scope: string): Promise<c.SmsRuntimeControl>;
  setRuntimeControl(input: c.SmsRuntimeControlInput): Promise<c.SmsRuntimeControl>;
  createTask(input: c.SmsTaskCreateInput): Promise<z.infer<typeof c.smsTaskAcceptedSchema>>;
  createChallenge(input: c.SmsChallengeCreateInput): Promise<z.infer<typeof c.smsTaskAcceptedSchema>>;
  verifyChallenge(id: string, input: c.SmsChallengeVerifyInput): Promise<z.infer<typeof c.smsChallengeResultSchema>>;
}

const base = "/admin/settings/sms";
const qs = (value: object) => new URLSearchParams(Object.entries(value).map(([key, item]) => [key, String(item)])).toString();
const json = (method: string, value: unknown) => ({ method, body: JSON.stringify(value) });
const idPath = (id: string) => `${base}/configurations/${encodeURIComponent(z.string().uuid().parse(id))}`;

export function createHttpSmsConfigurationApi(options: AdminHttpOptions): SmsConfigurationApi {
  const request = createAdminRequest(options);
  const requestConfiguration = (id: string, suffix = "", init?: RequestInit) => {
    const parsedId = z.string().uuid().parse(id);
    return request(`${idPath(parsedId)}${suffix}`, c.smsConfigurationSchema, init).then(configuration => {
      if (configuration.id !== parsedId) throw new M1ApiError("INVALID_RESPONSE", "返回的短信配置不属于当前请求，请重新加载后重试");
      return configuration;
    });
  };
  const requestBinding = (appId: string, init?: RequestInit) => {
    const parsedAppId = c.loginPolicyAppIdSchema.parse(appId);
    return request(`${base}/applications/${encodeURIComponent(parsedAppId)}/binding`, c.smsBindingSchema, init).then(binding => {
      if (binding.appId !== parsedAppId) throw new M1ApiError("INVALID_RESPONSE", "返回的短信关联不属于当前应用，请重新加载后重试");
      return binding;
    });
  };
  const control = (scope: string, init?: RequestInit) => {
    const parsed = c.smsRuntimeControlInputSchema.shape.scope.parse(scope);
    return request(`${base}/runtime/control${init ? "" : `?${qs({ scope: parsed })}`}`, c.smsRuntimeControlSchema, init).then(result => {
      if (result.scope !== parsed) throw new M1ApiError("INVALID_RESPONSE", "返回的停发状态不属于当前范围，请重新加载");
      return result;
    });
  };
  return {
    list: (query = {}) => request(`${base}/configurations?${qs(c.smsConfigurationListQuerySchema.parse(query))}`, c.smsConfigurationPageSchema),
    get: id => requestConfiguration(id),
    create: input => request(`${base}/configurations`, c.smsConfigurationSchema, json("POST", c.smsConfigurationCreateSchema.parse(input))),
    update: (id, input) => requestConfiguration(id, "", json("PATCH", c.smsConfigurationUpdateSchema.parse(input))),
    publish: (id, input) => requestConfiguration(id, "/publish", json("POST", c.smsConfigurationPublishSchema.parse(input))),
    rollback: (id, input) => requestConfiguration(id, "/rollback", json("POST", c.smsConfigurationRollbackSchema.parse(input))),
    setStatus: (id, input) => requestConfiguration(id, "/status", json("POST", c.smsConfigurationStatusSchema.parse(input))),
    revisions: (id, query = {}) => request(`${idPath(id)}/revisions?${qs(c.smsPaginationSchema.parse(query))}`, c.smsConfigurationRevisionPageSchema),
    getBinding: appId => requestBinding(appId),
    bind: (appId, input) => requestBinding(appId, json("PUT", c.smsBindingUpdateSchema.parse(input))),
    deliveries: (query = {}) => request(`${base}/deliveries?${qs(c.smsDeliveryQuerySchema.parse(query))}`, c.smsDeliveryPageSchema),
    runtime: () => request(`${base}/runtime`, c.smsRuntimeStatusSchema),
    runtimeSummary: () => request(`${base}/runtime/summary`, c.smsRuntimeSummarySchema),
    readiness: () => request(`${base}/readiness`, c.smsReadinessSchema),
    getRuntimeControl: scope => control(scope),
    setRuntimeControl: input => control(input.scope, json("POST", c.smsRuntimeControlInputSchema.parse(input))),
    createTask: input => request(`${base}/tasks`, c.smsTaskAcceptedSchema, json("POST", c.smsTaskCreateSchema.parse(input))),
    createChallenge: input => request(`${base}/challenges`, c.smsTaskAcceptedSchema, json("POST", c.smsChallengeCreateSchema.parse(input))),
    verifyChallenge: (id, input) => request(`${base}/challenges/${encodeURIComponent(z.uuid().parse(id))}/verify`, c.smsChallengeResultSchema, json("POST", c.smsChallengeVerifySchema.parse(input))),
  };
}
